import {
  STAGE_ORDER,
  STAGE_SPECS,
  type CaptureStage,
  type StageInvalidationReason,
} from '@kyc/shared';
import { ApiClient, ApiError, type ClaimResult, type StageView } from '../api/client.js';
import {
  closeCamera,
  openCamera,
  CameraPermissionError,
  CameraUnsupportedError,
  type CameraSession,
} from '../capture/camera.js';
import { InterruptionWatcher } from '../capture/interruptions.js';
import { StageRecorder } from '../capture/recorder.js';
import { MESSAGES } from '../config.js';
import { FaceEngine, ScriptedFaceEngine, isScriptedFaceMode, type FaceEngineLike } from '../liveness/face.js';
import { ChallengeTracker, PoseBaseline, POSE_TUNING, poseFrameFrom, type LivenessSample } from '../liveness/pose.js';
import { assessCardFrame, assessFacePhoto } from '../quality/assess.js';
import { AnalysisLoop, preflightFps, type AnalyzedFrame } from '../quality/engine.js';
import { BestFrameTracker, BlurPolicy, QualityClock, ReadyGate } from '../quality/gate.js';
import type { GrayFrame, Rect } from '../quality/metrics.js';
import { createCaptureScreen } from '../ui/captureScreen.js';
import {
  flipScreen,
  introScreen,
  loadingScreen,
  rejectionScreen,
  resultScreen,
  retryScreen,
  showScreen,
  uploadScreen,
} from '../ui/screens.js';
import { uploadStageBlob, UploadSupersededError } from '../upload/uploader.js';

const STAGE_LABELS: Record<CaptureStage, string> = {
  licence_front: 'Licence — Front',
  licence_back: 'Licence — Back',
  selfie_liveness: 'Selfie video',
};

/** Server statuses from which a fresh capture makes sense for a stage. */
const CAPTURABLE_STATUSES = new Set(['pending', 'rejected', 'invalidated', 'uploading']);
/** Server statuses the backend accepts a new upload-init from (uploads/service.ts). */
const UPLOADABLE_STATUSES = new Set(['pending', 'rejected', 'invalidated', 'uploading']);

interface CapturedTake {
  blob: Blob;
  recorderMime: string;
  hintsMs: number[];
}

/** §16 local acceptance counters, tracked over analyzed frames during recording. */
class RecordingStats {
  frames = 0;
  cardVisible = 0;
  passing = 0;
  private badRunStart: number | null = null;
  private badRunEnd = 0;
  maxBadRunMs = 0;

  push(timestampMs: number, cardPresent: boolean, pass: boolean): void {
    this.frames++;
    if (cardPresent) this.cardVisible++;
    if (pass) {
      this.passing++;
      if (this.badRunStart !== null) {
        this.maxBadRunMs = Math.max(this.maxBadRunMs, this.badRunEnd - this.badRunStart + 100);
        this.badRunStart = null;
      }
    } else {
      if (this.badRunStart === null) this.badRunStart = timestampMs;
      this.badRunEnd = timestampMs;
    }
  }

  finish(): void {
    if (this.badRunStart !== null) {
      this.maxBadRunMs = Math.max(this.maxBadRunMs, this.badRunEnd - this.badRunStart + 100);
      this.badRunStart = null;
    }
  }
}

export class CaptureFlow {
  private claim!: ClaimResult;
  private takes = new Map<CaptureStage, CapturedTake>();
  private faceEngine: FaceEngineLike | null = null;

  constructor(
    private root: HTMLElement,
    private api: ApiClient,
  ) {}

  async run(claimToken: string): Promise<void> {
    showScreen(this.root, loadingScreen('Loading your session…'));
    try {
      this.claim = await this.api.claim(claimToken, {
        platform: 'web',
        userAgent: navigator.userAgent.slice(0, 1000),
      });
    } catch (err) {
      showScreen(this.root, this.claimErrorScreen(err));
      return;
    }

    await new Promise<void>((resolve) => showScreen(this.root, introScreen(resolve)));

    try {
      await this.mainLoop();
    } catch (err) {
      if (err instanceof CameraPermissionError || err instanceof CameraUnsupportedError) {
        showScreen(this.root, resultScreen('error', err.message));
      } else if (err instanceof ApiError && err.code === 'SESSION_EXPIRED') {
        showScreen(this.root, resultScreen('expired'));
      } else {
        showScreen(this.root, resultScreen('error', (err as Error).message));
      }
    }
  }

  /** Map claim failures onto sensible outcomes (terminal sessions report their status). */
  private claimErrorScreen(err: unknown): HTMLElement {
    if (err instanceof ApiError) {
      if (err.code === 'SESSION_EXPIRED') return resultScreen('expired');
      if (err.code === 'SESSION_NOT_CLAIMABLE') {
        const status = (err.details as { status?: string } | undefined)?.status;
        if (status === 'approved') return resultScreen('approved');
        if (status === 'rejected') return resultScreen('rejected');
        return resultScreen('expired');
      }
    }
    return resultScreen('error', String((err as Error).message));
  }

  private async mainLoop(): Promise<void> {
    // Capture every capturable stage in §21 order, upload, then poll the server
    // verdicts; a rejected stage loops back into capture (§16 retake).
    for (;;) {
      const state = await this.api.getState();
      const pending = STAGE_ORDER.filter((s) => {
        const row = state.stages.find((r) => r.stage === s);
        return row && CAPTURABLE_STATUSES.has(row.status) && !this.takes.has(s);
      });

      for (const stage of pending) {
        if (stage === 'licence_back') {
          await new Promise<void>((resolve) => showScreen(this.root, flipScreen(resolve)));
        }
        const take = stage === 'selfie_liveness' ? await this.captureSelfie() : await this.captureCard(stage);
        this.takes.set(stage, take);
      }

      await this.uploadPendingTakes();

      const verdict = await this.pollVerdict();
      if (verdict.kind === 'retake') {
        this.takes.delete(verdict.stage);
        await new Promise<void>((resolve) =>
          showScreen(this.root, rejectionScreen(STAGE_LABELS[verdict.stage], verdict.reasons, resolve)),
        );
        continue;
      }
      showScreen(this.root, resultScreen(verdict.kind));
      return;
    }
  }

  /**
   * §21/§25 — upload every held take whose stage the server still accepts uploads for.
   * A transient failure surfaces a retry prompt (blobs stay in memory — nothing is
   * deleted before the server acknowledges completion); a stage already ahead on the
   * server (uploaded/validating/accepted) is skipped, not crashed into.
   */
  private async uploadPendingTakes(): Promise<void> {
    for (;;) {
      const state = await this.api.getState();
      const toUpload = STAGE_ORDER.filter((s) => {
        const row = state.stages.find((r) => r.stage === s);
        return this.takes.has(s) && row && UPLOADABLE_STATUSES.has(row.status);
      });
      // Takes for stages the server has already moved past are stale — drop them.
      for (const [stage] of this.takes) {
        const row = state.stages.find((r) => r.stage === stage);
        if (row && !UPLOADABLE_STATUSES.has(row.status)) this.takes.delete(stage);
      }
      if (toUpload.length === 0) return;

      const { node, rows } = uploadScreen(toUpload.map((s) => ({ stage: s, label: STAGE_LABELS[s] })));
      showScreen(this.root, node);

      try {
        for (const stage of toUpload) {
          const take = this.takes.get(stage)!;
          const row = rows.get(stage)!;
          row.setState('Uploading…');
          try {
            await uploadStageBlob(this.api, stage, take.blob, take.recorderMime, take.hintsMs, (p) =>
              row.setProgress(p.sentChunks / p.totalChunks),
            );
            row.setState('Upload complete', 'ok');
            row.setProgress(1);
            this.takes.delete(stage);
          } catch (err) {
            if (err instanceof UploadSupersededError) {
              // Server moved on (e.g. reload raced a completion) — treat as done here;
              // the poll loop will surface the real stage outcome.
              this.takes.delete(stage);
              row.setState('Already processed', 'ok');
              continue;
            }
            if (
              err instanceof ApiError &&
              ['STAGE_NOT_UPLOADABLE', 'STAGE_ALREADY_ACCEPTED', 'CONCURRENT_UPLOAD_INIT'].includes(err.code)
            ) {
              this.takes.delete(stage);
              row.setState('Already processed', 'ok');
              continue;
            }
            throw err;
          }
        }
        return;
      } catch (err) {
        if (err instanceof ApiError && err.status === 410) throw err;
        if (err instanceof ApiError && err.code === 'STAGE_ATTEMPTS_EXHAUSTED') {
          showScreen(this.root, resultScreen('rejected', 'No attempts left. Please contact support.'));
          throw err;
        }
        // §25 network failure → keep files, let the user resume the upload.
        await new Promise<void>((resolve) =>
          showScreen(this.root, retryScreen('Upload did not complete. Check your connection and try again.', resolve)),
        );
      }
    }
  }

  private async pollVerdict(): Promise<
    { kind: 'retake'; stage: CaptureStage; reasons: string[] } | { kind: 'submitted' | 'approved' | 'rejected' | 'expired' }
  > {
    showScreen(this.root, loadingScreen('Verifying your videos…'));
    for (;;) {
      const state = await this.api.getState().catch((err) => {
        if (err instanceof ApiError && err.code === 'SESSION_EXPIRED') return null;
        throw err;
      });
      if (!state) return { kind: 'expired' };

      const rejected = state.stages.find((s) => s.status === 'rejected');
      if (rejected) return { kind: 'retake', stage: rejected.stage, reasons: rejected.rejectReasons };
      if (state.status === 'approved') return { kind: 'approved' };
      if (state.status === 'rejected') return { kind: 'rejected' };
      if (state.status === 'in_review') return { kind: 'submitted' };
      await new Promise((r) => setTimeout(r, 2500));
    }
  }

  // -------------------------------------------------------------------------
  // Licence capture — §16/§17 client state machine
  // -------------------------------------------------------------------------

  private async captureCard(stage: CaptureStage): Promise<CapturedTake> {
    const spec = STAGE_SPECS[stage];
    const screen = createCaptureScreen('card', false);
    showScreen(this.root, screen.root);
    screen.setStep(`Step ${stage === 'licence_front' ? 1 : 2} of 3`);
    screen.setTitle(stage === 'licence_front' ? 'DRIVING LICENCE — FRONT' : 'DRIVING LICENCE — BACK');

    const camera = await this.openWithPreflight('rear', screen.video);
    try {
      screen.drawOverlay();
      window.addEventListener('resize', screen.drawOverlay);
      await this.api.postStageEvent(stage, { type: 'stage_started' }).catch(() => undefined);

      return await new Promise<CapturedTake>((resolve, reject) => {
        const recorder = new StageRecorder(camera.stream);
        const gate = new ReadyGate();
        const blur = new BlurPolicy();
        const best = new BestFrameTracker();
        const clock = new QualityClock();
        let stats = new RecordingStats();
        let phase: 'ready' | 'countdown' | 'recording' | 'stopping' = 'ready';
        let countdownEndsAt = 0;
        let cardMissingStreak = 0;
        let prevGray: GrayFrame | null = null;
        let wallClockStop: number | null = null;
        let settled = false;

        const settle = (fn: () => void) => {
          if (settled) return;
          settled = true;
          loop.stop();
          watcher.dispose();
          fn();
        };

        const resetToReady = (message: string) => {
          if (phase === 'recording') recorder.cancel();
          if (wallClockStop !== null) {
            window.clearTimeout(wallClockStop);
            wallClockStop = null;
          }
          phase = 'ready';
          gate.reset();
          blur.reset();
          best.reset();
          clock.reset();
          stats = new RecordingStats();
          cardMissingStreak = 0;
          screen.setCountdown(null);
          screen.setProgress(0);
          screen.setTimer('');
          screen.setMessage(message, 'warn');
        };

        const abortTake = async (reason: StageInvalidationReason, message: string) => {
          resetToReady(message);
          await this.api.postStageEvent(stage, { type: 'retake', reason }).catch(() => undefined);
        };

        // §16 LOCAL_VALIDATION — runs exactly once per take (phase 'stopping' guards
        // re-entry so a second analyzed frame can never race a truncated stop).
        const finishRecording = async () => {
          if (phase !== 'recording') return;
          phase = 'stopping';
          if (wallClockStop !== null) {
            window.clearTimeout(wallClockStop);
            wallClockStop = null;
          }
          stats.finish();
          let blob: Blob;
          try {
            blob = await recorder.stop();
          } catch {
            void abortTake('user_retake', 'Recording failed — please try again');
            return;
          }
          const visibleRatio = stats.frames > 0 ? stats.cardVisible / stats.frames : 0;
          const passRatio = stats.frames > 0 ? stats.passing / stats.frames : 0;
          // Handheld-calibrated local acceptance; the backend re-checks authoritatively (§22).
          const ok = visibleRatio >= 0.75 && passRatio >= 0.6 && stats.maxBadRunMs <= 1_000 && blob.size > 0;
          if (!ok) {
            void abortTake('local_quality_failed', 'Quality was not good enough — please record again');
            return;
          }
          settle(() => resolve({ blob, recorderMime: recorder.mimeType, hintsMs: best.hintsMs }));
        };

        const watcher = new InterruptionWatcher((reason) => {
          if (phase === 'stopping') return; // take already complete
          if (phase === 'recording') {
            void this.api.postStageEvent(stage, { type: 'invalidated', reason }).catch(() => undefined);
          }
          resetToReady('Interrupted — please reposition');
        });
        watcher.watch(camera.track);

        const loop = new AnalysisLoop(screen.video, (frame: AnalyzedFrame) => {
          if (phase === 'stopping') return;
          const box: Rect = screen.overlayRectFor(frame.gray.width, frame.gray.height);
          const assessment = assessCardFrame(frame.gray, prevGray, box, frame.mediaTimeMs);
          prevGray = frame.gray;

          screen.setChips([
            { label: 'Card detected', on: assessment.cardPresent },
            { label: 'Focus clear', on: assessment.verdict.sharp && assessment.cardPresent },
            { label: 'Lighting good', on: assessment.verdict.pass || assessment.verdict.sharp },
          ]);

          if (phase === 'ready') {
            gate.push(assessment.verdict.pass);
            screen.setMessage(assessment.verdict.pass ? MESSAGES.ready : assessment.message, assessment.verdict.pass ? 'ok' : 'info');
            if (gate.satisfied) {
              phase = 'countdown';
              countdownEndsAt = performance.now() + gate.countdownMs;
            }
            return;
          }

          if (phase === 'countdown') {
            if (!assessment.verdict.pass) {
              // §10 — stability must hold through the countdown.
              phase = 'ready';
              gate.reset();
              screen.setCountdown(null);
              screen.setMessage(assessment.message, 'warn');
              return;
            }
            const left = countdownEndsAt - performance.now();
            screen.setCountdown(String(Math.max(1, Math.ceil(left / 1000))));
            if (left <= 0) {
              screen.setCountdown(null);
              phase = 'recording';
              recorder.start(frame.mediaTimeMs);
              screen.setMessage(MESSAGES.recording, 'ok');
              void this.api.postStageEvent(stage, { type: 'recording_started' }).catch(() => undefined);
              // §20 wall-clock backstop in case analysis stalls.
              wallClockStop = window.setTimeout(() => void finishRecording(), spec.nominalDurationMs + 700);
            }
            return;
          }

          // phase === 'recording'
          const recMs = frame.mediaTimeMs - recorder.startMediaTimeMs;
          screen.setTimer(`00:${String(Math.floor(Math.min(recMs, spec.nominalDurationMs) / 1000)).padStart(2, '0')} / 0:${spec.nominalDurationMs / 1000}`);
          screen.setProgress(recMs / spec.nominalDurationMs);

          stats.push(recMs, assessment.cardPresent, assessment.verdict.pass);
          clock.push(recMs, assessment.verdict.pass);
          best.push({ ...assessment.verdict, timestampMs: recMs });

          cardMissingStreak = assessment.cardPresent ? 0 : cardMissingStreak + 1;
          if (cardMissingStreak >= 3) {
            // §11 — card left the frame.
            void abortTake('card_lost', MESSAGES.cardMissing);
            return;
          }

          const action = blur.push(recMs, assessment.verdict.sharp);
          if (action === 'abort_take') {
            void abortTake('sustained_blur', MESSAGES.blur);
            return;
          }
          if (action === 'warn_refocus' || action === 'at_risk') {
            screen.setMessage(MESSAGES.blur, 'warn');
          } else if (assessment.verdict.pass) {
            screen.setMessage(MESSAGES.recording, 'ok');
          }

          // §20 — stop at the first frame timestamp ≥ nominal duration.
          if (recMs >= spec.nominalDurationMs) {
            void finishRecording();
          }
        });

        screen.onRetake(() => {
          if (phase !== 'stopping') void abortTake('user_retake', 'Please reposition');
        });
        screen.setMessage(MESSAGES.cardMissing);
        loop.start();

        // Session expiry safeguard: capture cannot run past the session window.
        const expiryMs = new Date(this.claim.expiresAt).getTime() - Date.now();
        if (expiryMs > 0 && expiryMs < 24 * 60 * 60 * 1000) {
          window.setTimeout(() => settle(() => reject(new ApiError(410, 'SESSION_EXPIRED', 'Session expired'))), expiryMs);
        }
      });
    } finally {
      window.removeEventListener('resize', screen.drawOverlay);
      closeCamera(camera);
      screen.dispose();
    }
  }

  // -------------------------------------------------------------------------
  // Selfie/liveness capture — §18 (self-calibrating pose baseline)
  // -------------------------------------------------------------------------

  private async captureSelfie(): Promise<CapturedTake> {
    const policy = this.claim.capturePolicy.livenessPolicy;
    const screen = createCaptureScreen('face', true);
    showScreen(this.root, screen.root);
    screen.setStep('Step 3 of 3');
    screen.setTitle('SELFIE VIDEO');
    screen.setMessage('Loading face model…');

    if (!this.faceEngine) {
      this.faceEngine = isScriptedFaceMode() ? new ScriptedFaceEngine() : new FaceEngine();
      await this.faceEngine.load();
    }
    const face = this.faceEngine;
    // A fresh camera stream restarts mediaTime near 0 — reset the monotonic guard.
    face.resetForNewStream();

    const camera = await this.openWithPreflight('front', screen.video);
    try {
      screen.drawOverlay();
      window.addEventListener('resize', screen.drawOverlay);
      await this.api.postStageEvent('selfie_liveness', { type: 'stage_started' }).catch(() => undefined);

      return await new Promise<CapturedTake>((resolve, reject) => {
        const recorder = new StageRecorder(camera.stream);
        const gate = new ReadyGate();
        const best = new BestFrameTracker();
        let tracker = new ChallengeTracker(this.claim.livenessChallenge);
        // §18 — per-session pose baseline: "frontal" is judged relative to how THIS user
        // holds their phone, not an absolute landmark ratio.
        let baseline = new PoseBaseline();
        let widen = 1;
        let stuckSinceMs: number | null = null;
        let phase: 'ready' | 'countdown' | 'recording' | 'stopping' = 'ready';
        let countdownEndsAt = 0;
        let faceLostStreak = 0;
        let prevGray: GrayFrame | null = null;
        let settled = false;

        const settle = (fn: () => void) => {
          if (settled) return;
          settled = true;
          loop.stop();
          watcher.dispose();
          fn();
        };

        const resetToReady = (message: string) => {
          if (phase === 'recording') recorder.cancel();
          phase = 'ready';
          gate.reset();
          best.reset();
          tracker = new ChallengeTracker(this.claim.livenessChallenge);
          baseline = new PoseBaseline();
          widen = 1;
          stuckSinceMs = null;
          faceLostStreak = 0;
          screen.setCountdown(null);
          screen.setProgress(0);
          screen.setTimer('');
          screen.setMessage(message, 'warn');
        };

        const abortTake = async (reason: StageInvalidationReason, message: string) => {
          resetToReady(message);
          await this.api.postStageEvent('selfie_liveness', { type: 'retake', reason }).catch(() => undefined);
        };

        const finishRecording = async () => {
          if (phase !== 'recording') return;
          phase = 'stopping';
          let blob: Blob;
          try {
            blob = await recorder.stop();
          } catch {
            void abortTake('user_retake', 'Recording failed — please try again');
            return;
          }
          settle(() => resolve({ blob, recorderMime: recorder.mimeType, hintsMs: best.hintsMs }));
        };

        const watcher = new InterruptionWatcher((reason) => {
          if (phase === 'stopping') return;
          if (phase === 'recording') {
            void this.api.postStageEvent('selfie_liveness', { type: 'invalidated', reason }).catch(() => undefined);
          }
          resetToReady('Interrupted — look at the centre again');
        });
        watcher.watch(camera.track);

        const instructionFor = (): string => {
          const p = tracker.current;
          switch (p.kind) {
            case 'straight_hold':
              return MESSAGES.faceLookStraight;
            case 'move':
              return {
                left: MESSAGES.moveLeft,
                right: MESSAGES.moveRight,
                up: MESSAGES.moveUp,
                down: MESSAGES.moveDown,
              }[p.direction];
            case 'return_center':
              return MESSAGES.faceReturnCenter;
            case 'done':
              return 'Hold still';
          }
        };

        const loop = new AnalysisLoop(screen.video, (frame: AnalyzedFrame) => {
          if (phase === 'stopping') return;
          // §7 — the drawn oval mapped into normalized frame coordinates.
          const ovalPx = screen.overlayRectFor(frame.gray.width, frame.gray.height);
          const oval = {
            x: ovalPx.x / frame.gray.width,
            y: ovalPx.y / frame.gray.height,
            w: ovalPx.w / frame.gray.width,
            h: ovalPx.h / frame.gray.height,
          };
          // E2E seam: the scripted engine reads the current liveness phase from here.
          (window as unknown as { __kycLivenessPhase?: string }).__kycLivenessPhase =
            tracker.current.kind === 'move' ? `move:${tracker.current.direction}` : tracker.current.kind;
          const obs = face.detect(screen.video, frame.mediaTimeMs, oval);
          const faceBox: Rect | null = obs.box
            ? {
                x: obs.box.x * frame.gray.width,
                y: obs.box.y * frame.gray.height,
                w: obs.box.w * frame.gray.width,
                h: obs.box.h * frame.gray.height,
              }
            : null;
          const photo = faceBox ? assessFacePhoto(frame.gray, prevGray, faceBox) : null;
          prevGray = frame.gray;

          const facePresent = obs.faceCount === 1 && obs.faceInOval && obs.eyesVisible;
          // Scripted mode drives a synthetic face over a non-face test stream, so the
          // photo gates (which would judge the test pattern) are bypassed there only.
          const photoOk = face.scripted ? facePresent : !!photo && photo.sharp && photo.lightingOk && photo.motionOk;

          // Calibrate the baseline from any single-face-in-oval sample.
          if (facePresent && obs.pose) baseline.add(obs.pose);
          const poseFrame = obs.pose && baseline.ready ? poseFrameFrom(obs.pose, baseline, widen) : null;
          const frameValid = facePresent && photoOk && (poseFrame?.frontal ?? false);

          screen.setDebug(
            `faces:${obs.faceCount} oval:${obs.faceInOval ? 1 : 0} eyes:${obs.eyesVisible ? 1 : 0} ` +
              `photo:${photoOk ? 1 : 0} base:${baseline.sampleCount} widen:${widen.toFixed(1)} ` +
              `yaw:${poseFrame ? poseFrame.yawDelta.toFixed(2) : '—'} pitch:${poseFrame ? poseFrame.pitchDelta.toFixed(2) : '—'} ` +
              `frontal:${poseFrame?.frontal ? 1 : 0} phase:${phase}/${tracker.current.kind}`,
          );

          screen.setChips([
            { label: 'Face detected', on: obs.faceCount === 1 },
            { label: 'In oval', on: obs.faceInOval },
            { label: 'Clear', on: !!photo && photo.sharp && photo.lightingOk },
          ]);

          if (phase === 'ready') {
            gate.push(frameValid);
            screen.setTimer(`0:00 / 0:${Math.round(policy.targetTotalDurationMs / 1000)}`);
            screen.setMessage(
              obs.faceCount === 0
                ? MESSAGES.faceMissing
                : obs.faceCount > 1
                  ? MESSAGES.faceMultiple
                  : !obs.faceInOval
                    ? MESSAGES.faceMissing
                    : !baseline.ready
                      ? MESSAGES.faceLookStraight
                      : frameValid
                        ? MESSAGES.faceLookStraight
                        : poseFrame && !poseFrame.frontal
                          ? MESSAGES.faceLookStraight
                          : MESSAGES.lowLight,
              frameValid ? 'ok' : 'info',
            );
            // §25 recovery — if the user is present but the gate will not open, widen the
            // frontal band progressively instead of leaving them stuck forever.
            if (facePresent && !gate.satisfied) {
              stuckSinceMs = stuckSinceMs ?? frame.mediaTimeMs;
              if (frame.mediaTimeMs - stuckSinceMs > 5_000 && widen < POSE_TUNING.widenMax) {
                widen = Math.min(POSE_TUNING.widenMax, widen * POSE_TUNING.widenStep);
                stuckSinceMs = frame.mediaTimeMs;
              }
            } else if (!facePresent) {
              stuckSinceMs = null;
            }
            if (gate.satisfied) {
              phase = 'countdown';
              countdownEndsAt = performance.now() + gate.countdownMs;
            }
            return;
          }

          if (phase === 'countdown') {
            if (!frameValid) {
              phase = 'ready';
              gate.reset();
              screen.setCountdown(null);
              return;
            }
            const left = countdownEndsAt - performance.now();
            screen.setCountdown(String(Math.max(1, Math.ceil(left / 1000))));
            if (left <= 0) {
              screen.setCountdown(null);
              phase = 'recording';
              recorder.start(frame.mediaTimeMs);
              void this.api.postStageEvent('selfie_liveness', { type: 'recording_started' }).catch(() => undefined);
            }
            return;
          }

          // phase === 'recording'
          const recMs = frame.mediaTimeMs - recorder.startMediaTimeMs;
          screen.setTimer(`00:${String(Math.floor(recMs / 1000)).padStart(2, '0')} / 0:${Math.round(policy.targetTotalDurationMs / 1000)}`);
          screen.setProgress(recMs / policy.targetTotalDurationMs);

          // §18 — hard timeout backstop for a stalled attempt.
          if (recMs >= policy.hardTimeoutMs) {
            void abortTake('hard_timeout', 'Time is up — please try again');
            return;
          }

          faceLostStreak = obs.faceCount === 1 ? 0 : faceLostStreak + 1;
          if (faceLostStreak >= 15) {
            void abortTake('face_lost', MESSAGES.faceMissing);
            return;
          }

          const sample: LivenessSample = {
            timestampMs: recMs,
            faceCount: obs.faceCount,
            faceInOval: obs.faceInOval,
            eyesVisible: obs.eyesVisible,
            photoOk,
            pose: obs.pose && baseline.ready ? poseFrameFrom(obs.pose, baseline, widen) : null,
          };
          const phaseNow = tracker.push(sample);
          if (phaseNow.kind === 'straight_hold') {
            screen.setMessage(`${MESSAGES.faceLookStraight}  ${(phaseNow.validMs / 1000).toFixed(1)} / ${(phaseNow.requiredMs / 1000).toFixed(0)}s`, 'ok');
            if (frameValid) best.push({ timestampMs: recMs, pass: true, sharp: true, rankScore: 1 });
          } else {
            screen.setMessage(instructionFor(), phaseNow.kind === 'done' ? 'ok' : 'info');
          }

          // §18/§20 — duration windows.
          const done = phaseNow.kind === 'done';
          if (done && recMs >= policy.minimumTotalDurationMs) {
            void finishRecording();
            return;
          }
          if (recMs >= policy.maximumNormalDurationMs) {
            if (done) void finishRecording();
            else void abortTake('hard_timeout', 'Time is up — please try again');
            return;
          }
        });

        screen.onRetake(() => {
          if (phase !== 'stopping') void abortTake('user_retake', 'Look at the centre again');
        });
        screen.setMessage(MESSAGES.faceMissing);
        loop.start();

        const expiryMs = new Date(this.claim.expiresAt).getTime() - Date.now();
        if (expiryMs > 0 && expiryMs < 24 * 60 * 60 * 1000) {
          window.setTimeout(() => settle(() => reject(new ApiError(410, 'SESSION_EXPIRED', 'Session expired'))), expiryMs);
        }
      });
    } finally {
      window.removeEventListener('resize', screen.drawOverlay);
      closeCamera(camera);
      screen.dispose();
    }
  }

  // -------------------------------------------------------------------------
  // §13 — open camera, attach to video, sample real FPS (advisory).
  // -------------------------------------------------------------------------

  private async openWithPreflight(kind: 'rear' | 'front', video: HTMLVideoElement): Promise<CameraSession> {
    const camera = await openCamera(kind, this.claim.capturePolicy);
    video.srcObject = camera.stream;
    await video.play().catch(() => undefined);

    // §13/§22 — the preflight is advisory only. It is measured after a warm-up window
    // (cameras under-deliver while ramping up), and the authoritative FPS SLA is
    // recomputed server-side on the real recording's timestamps. We therefore never
    // hard-block capture here: a modern phone must not be turned away by a noisy 2-second
    // sample, and a genuinely low-FPS device on the low-assurance web path should still
    // be able to record and route to manual review.
    const pf = await preflightFps(video).catch(() => null);
    if (pf?.measured && pf.averageFps > 0 && pf.averageFps < 20) {
      // Usually low light — the runtime lighting gate guides the user; just record it.
      console.info(`[kyc] preflight fps low: ${pf.averageFps}`);
    }
    return camera;
  }
}
